// app/javascript/projects/components/ProjectsDisplay.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSignOut } from 'hooks/auth/mutations'
import { useCurrentUser } from 'hooks/users/queries'
import { usePlans } from 'hooks/subscriptions/queries'
import { getSubscriptionPricing } from 'utils/subscriptionUtils'
import couponMapper from 'mappers/CouponMapper'
import couponErrorMapper from 'mappers/CouponErrorMapper'
import trackingUtil from 'utils/TrackingUtil'
import SessionAPI from 'apis/SessionAPI'
import sessionMapper from 'mappers/SessionMapper'
import SubscriptionPresentation from 'sign_up/SubscriptionPresentation'
import CouponAPI from 'apis/CouponAPI'

const queryString = require('query-string');

const SubscriptionDisplay = (props) => {
  const { data: user } = useCurrentUser();
  const { data: plansData } = usePlans();
  const [success, setSuccess] = useState(null);
  const [promoError, setPromoError] = useState(null);
  const [stripeError, setStripeError] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const sessionRef = useRef(null);
  const yearSessionRef = useRef(null);

  const isYearly = queryString.parse(props.search)?.plan;

  // Create sessions with or without coupon
  const createSessions = useCallback((couponId) => {
    sessionRef.current = SessionAPI.createSession(
      sessionMapper({ coupon_id: couponId })
    ).catch((error) => {
      return { error };
    });

    yearSessionRef.current = SessionAPI.createSession(
      sessionMapper({ interval: 'year', coupon_id: couponId })
    ).catch((error) => {
      return { error };
    });
  }, []);

  // Handle promo code validation and session creation
  useEffect(() => {
    if (!props.promo) {
      // No promo, just create sessions without coupon
      createSessions(null);
      return;
    }

    // Validate and apply promo code
    CouponAPI.getPromo(props.promo)
      .then((coupon) => {
        const mappedSuccess = couponMapper(props.promo, coupon);
        if (!mappedSuccess) return;
        setSuccess(mappedSuccess);
        trackingUtil.updateAttribute('last_promo_code', props.promo);
        createSessions(props.promo);
      })
      .catch((e) => {
        const mappedPromoError = couponErrorMapper(props.promo, e);
        if (mappedPromoError) {
          setPromoError(mappedPromoError);
        }
        // Even if promo fails, create sessions without it
        createSessions(null);
      });
  }, [props.promo, createSessions]);

  // Track page view on mount
  useEffect(() => {
    trackingUtil.logAmplitudeEvent('subscribe_page_viewed');
  }, []);

  const signOutMutation = useSignOut();
  
  const signOut = () => {
    signOutMutation.mutate();
  };

  const subscribe = async () => {
    setLoading(true);

    const session = await (isYearly ? yearSessionRef.current : sessionRef.current);

    if (session?.error) {
      setStripeError('Something went wrong. Please refresh and try again.');
      setLoading(false);
      return;
    }

    const stripe = Stripe(STRIPE_PUBLISHABLE_KEY);
    stripe.redirectToCheckout({
      sessionId: session.data.id
    });
  };


  const getError = () => {
    if (!!promoError && promoError.error)
      return promoError;

    if (!!stripeError) {
      return {
        error: true,
        message: stripeError
      };
    }

    return null;
  };

  const error = getError();
  
  // Get all pricing calculations from utility
  const { displayPrice, annualAmount, discount } = getSubscriptionPricing(plansData, !!isYearly);

  return (
    <SubscriptionPresentation
      error={error}
      success={success}
      planPrice={displayPrice}
      discount={discount}
      yearly={!!isYearly}
      annual_plan_amount={annualAmount}
      userEmail={user?.email}
      loading={loading}
      onSubscribe={subscribe}
      onSignOut={signOut}
    />
  );
};

export default SubscriptionDisplay;
